import { makeStyles, tokens, Text, mergeClasses } from '@fluentui/react-components';
import { CheckmarkCircle20Filled, ArrowRight20Regular, Circle20Regular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useTelemetry } from '@/hooks/useTelemetry';
import type { OnboardingStepId } from '@/hooks/useOnboardingProgress';

export type StepStatus = 'completed' | 'current' | 'upcoming';

interface OnboardingStepProps {
  stepId: OnboardingStepId;
  title: string;
  description: string;
  route: string;
  status: StepStatus;
  stepNumber: number;
  animationDelay: number;
  /** When provided, called instead of navigating to route */
  onAction?: () => void;
}

const useStyles = makeStyles({
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    minHeight: '40px',
    border: `1px solid transparent`,
    transitionProperty: 'background-color, border-color, transform, opacity',
    transitionDuration: '150ms',
    transitionTimingFunction: 'ease-out',
    // Stagger entry animation
    opacity: 0,
    transform: 'translateY(6px)',
    animationName: {
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    animationDuration: '250ms',
    animationTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)',
    animationFillMode: 'forwards',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
      opacity: 1,
      transform: 'none',
    },
  },
  stepCompleted: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':active': {
      transform: 'scale(0.97)',
    },
  },
  stepCurrent: {
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':active': {
      transform: 'scale(0.97)',
    },
  },
  stepUpcoming: {
    opacity: 0,
    animationName: {
      to: { opacity: 0.4, transform: 'translateY(0)' },
    },
    cursor: 'default',
    '@media (prefers-reduced-motion: reduce)': {
      opacity: 0.4,
    },
  },

  // Status icon
  statusIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
  },
  iconCompleted: {
    color: tokens.colorPaletteGreenForeground1,
  },
  iconCurrent: {
    color: tokens.colorBrandForeground1,
  },
  iconUpcoming: {
    color: tokens.colorNeutralForeground4,
  },

  // Content
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  titleCompleted: {
    color: tokens.colorNeutralForeground3,
    textDecorationLine: 'line-through',
  },
  titleUpcoming: {
    color: tokens.colorNeutralForeground4,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase200,
  },
  descriptionUpcoming: {
    color: tokens.colorNeutralForeground4,
  },
  buttonReset: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: 'inherit',
    textAlign: 'start' as const,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    width: '100%',
    cursor: 'inherit',
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
    },
  },
});

const statusIcons: Record<StepStatus, React.ElementType> = {
  completed: CheckmarkCircle20Filled,
  current: ArrowRight20Regular,
  upcoming: Circle20Regular,
};

export const OnboardingStep = ({
  stepId,
  title,
  description,
  route,
  status,
  stepNumber,
  animationDelay,
  onAction,
}: OnboardingStepProps) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { trackEvent } = useTelemetry();

  const StatusIcon = statusIcons[status];
  const isNavigable = status !== 'upcoming';

  const handleClick = () => {
    if (isNavigable) {
      trackEvent('onboarding.step.clicked', { stepId, stepNumber, status });
      if (onAction) {
        onAction();
      } else if (/^https?:\/\//i.test(route)) {
        window.open(route, '_blank', 'noopener,noreferrer');
      } else {
        navigate(route);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isNavigable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleClick();
    }
  };

  const stepClassName = mergeClasses(
    styles.step,
    status === 'completed' && styles.stepCompleted,
    status === 'current' && styles.stepCurrent,
    status === 'upcoming' && styles.stepUpcoming
  );

  const titleClassName = mergeClasses(
    styles.title,
    status === 'completed' && styles.titleCompleted,
    status === 'upcoming' && styles.titleUpcoming
  );

  const descClassName = mergeClasses(
    styles.description,
    status === 'upcoming' && styles.descriptionUpcoming
  );

  return (
    <li style={{ animationDelay: `${animationDelay}ms` }} className={stepClassName}>
      <button
        type="button"
        className={styles.buttonReset}
        tabIndex={isNavigable ? 0 : -1}
        aria-label={`Step ${stepNumber}: ${title} — ${status}`}
        aria-current={status === 'current' ? 'step' : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={!isNavigable}
      >
        <span className={mergeClasses(
          styles.statusIcon,
          status === 'completed' && styles.iconCompleted,
          status === 'current' && styles.iconCurrent,
          status === 'upcoming' && styles.iconUpcoming,
        )}>
          <StatusIcon aria-hidden="true" />
        </span>

        <div className={styles.content}>
          <Text className={titleClassName}>{title}</Text>
          <Text className={descClassName}>{description}</Text>
        </div>
      </button>
    </li>
  );
};
